/* eslint max-lines: ["error", 700], max-len: 0 */
import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { createRef, RefObject, useEffect, useState } from 'react';
import CopyIcon from '../../../base-ui/components/icon/CopyIcon';
import SpinnerIcon from '../../../main/component/icon/SpinnerIcon';
import useAlterBookingState from '../../hooks/useAlterBookingState';

interface NtakImportData {
    checkin: string;
    checkout: string;
    revenue: number;
    guestName: string;
    nationalityHU: string;
}

interface NtakImportResponse {
    success: boolean;
    error?: string;
}

interface QueueAddData {
    commandType: string;
    name: string;
    targetDomain: string;
    targetUrl: string;
    data: NtakImportData;
    sourceBookingId?: number;
}

interface QueueAddResponse {
    success: boolean;
    error?: string;
    command?: {
        id: string;
        name: string;
    };
}

declare global {
    interface Window {
        // Extension injects its ID here for detection and messaging
        __MUSHROOM_EXTENSION_ID__?: string;
    }
    // Chrome runtime API types for external messaging
    const chrome: {
        runtime?: {
            sendMessage: (
                extensionId: string,
                message: { type: string; data?: NtakImportData } & Partial<QueueAddData>,
                callback: (response: NtakImportResponse | QueueAddResponse) => void
            ) => void;
        };
    } | undefined;
}

const getExtensionId = (): string | null => {
    return typeof window !== 'undefined' ? window.__MUSHROOM_EXTENSION_ID__ ?? null : null;
};

const sendNtakImport = (data: NtakImportData): Promise<NtakImportResponse> => {
    return new Promise((resolve) => {
        const extensionId = getExtensionId();
        if (!extensionId) {
            resolve({ success: false, error: 'Extension not detected' });
            return;
        }
        if (typeof chrome === 'undefined' || !chrome?.runtime?.sendMessage) {
            resolve({ success: false, error: 'Chrome runtime not available' });
            return;
        }
        chrome.runtime.sendMessage(
            extensionId,
            { type: 'NTAK_IMPORT', data },
            (response) => {
                if (response) {
                    resolve(response);
                } else {
                    resolve({ success: false, error: 'No response from extension' });
                }
            }
        );
    });
};

const sendQueueCommand = (queueData: QueueAddData): Promise<QueueAddResponse> => {
    return new Promise((resolve) => {
        const extensionId = getExtensionId();
        if (!extensionId) {
            resolve({ success: false, error: 'Extension not detected' });
            return;
        }
        if (typeof chrome === 'undefined' || !chrome?.runtime?.sendMessage) {
            resolve({ success: false, error: 'Chrome runtime not available' });
            return;
        }
        // Construct message with 'type' as QUEUE_ADD and include command data with its own type
        const message = {
            type: 'QUEUE_ADD' as const,
            ...queueData,
        };
        chrome.runtime.sendMessage(
            extensionId,
            message,
            (response) => {
                if (response) {
                    resolve(response as QueueAddResponse);
                } else {
                    resolve({ success: false, error: 'No response from extension' });
                }
            }
        );
    });
};

const TextCopyInput = ({
    value,
    type,
}: {
    value: string;
    type?: 'textarea';
}) => {
    const [copied, setCopied] = useState(false);
    const inputRef = createRef<HTMLInputElement | HTMLTextAreaElement>();

    const handleCopy = async () => {
        inputRef.current?.select();
        try {
            await navigator.clipboard.writeText(value);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy text:', err);
        }
    };

    const inputStyles = {
        width: '100%',
        padding: '12px',
        backgroundColor: '#0f0f23',
        border: copied ? '2px solid #22c55e' : '2px solid #374151',
        color: '#fff',
        fontFamily: 'monospace',
        fontSize: '0.75rem',
        resize: 'none' as const,
        transition: 'border-color 0.2s ease',
    };

    return (
        <Box display="flex" flexDirection="column" gap={2}>
            {type === 'textarea' ? (
                <textarea
                    value={value}
                    readOnly
                    ref={inputRef as RefObject<HTMLTextAreaElement>}
                    rows={15}
                    style={inputStyles}
                />
            ) : (
                <input
                    value={value}
                    type="text"
                    readOnly
                    ref={inputRef as RefObject<HTMLInputElement>}
                    style={inputStyles}
                />
            )}
            <Button
                variant="contained"
                color={copied ? 'success' : 'secondary'}
                onClick={handleCopy}
                startIcon={<CopyIcon />}
                sx={{
                    borderRadius: 0,
                    border: '2px solid',
                    borderColor: copied ? 'success.main' : 'secondary.main',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    px: 3,
                    py: 1,
                }}
            >
                {copied ? 'Copied!' : 'Copy Code'}
            </Button>
        </Box>
    );
};

const GuestBookView = ({
    bookingId,
    onClose,
    onSuccess,
}: {
    bookingId: number;
    onClose: () => void;
    onSuccess?: () => void;
}) => {
    const [extensionInstalled, setExtensionInstalled] = useState(false);
    const [importStatus, setImportStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
    const [importError, setImportError] = useState<string | null>(null);
    const [showManualFallback, setShowManualFallback] = useState(false);
    const [queueStatus, setQueueStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');

    useEffect(() => {
        // Check for extension after a short delay to allow content script to inject
        const timer = setTimeout(() => {
            setExtensionInstalled(getExtensionId() !== null);
        }, 100);
        return () => clearTimeout(timer);
    }, []);

    const { data, error } = useQuery<{
        booking: {
            country: {
                nameHU: string;
                nationalityHU: string;
            };
            checkin: string;
            checkout: string;
            revenue: number;
            cleaningFee: number;
            guestName: string;
        };
    }>(
        gql`
            query GuestBookViewDialogQuery($id: Int!) {
                booking(id: $id) {
                    id
                    country {
                        nameHU
                        nationalityHU
                    }
                    checkin
                    checkout
                    revenue
                    cleaningFee
                    guestName
                }
            }
        `,
        { variables: { id: bookingId } }
    );

    const [alterBookingState, { loading: alterBookingStateLoading }] =
        useAlterBookingState(bookingId);

    const handleExtensionImport = async () => {
        if (!data) return;

        setImportStatus('loading');
        setImportError(null);

        const { booking: { country, checkin, checkout, revenue, guestName } } = data;

        const response = await sendNtakImport({
            checkin,
            checkout,
            revenue,
            guestName,
            nationalityHU: country.nationalityHU,
        });

        if (response.success) {
            setImportStatus('success');
            // Auto-mark as imported after successful extension import
            await alterBookingState({
                variables: {
                    id: bookingId,
                    fields: {
                        importedToGovernmentPortal: true,
                    },
                },
            });
            onSuccess?.();
            setTimeout(() => onClose(), 1500);
        } else {
            setImportStatus('error');
            setImportError(response.error ?? 'Import failed');
        }
    };

    const handleQueueAction = async () => {
        if (!data) return;

        setQueueStatus('loading');
        setImportError(null);

        const { booking: { country, checkin, checkout, revenue, guestName } } = data;

        const response = await sendQueueCommand({
            commandType: 'NTAK_IMPORT',
            name: `${guestName} (${checkin})`,
            targetDomain: 'vendegem.hu',
            targetUrl: 'https://vendegem.hu/',
            data: {
                checkin,
                checkout,
                revenue,
                guestName,
                nationalityHU: country.nationalityHU,
            },
            sourceBookingId: bookingId,
        });

        if (response.success) {
            setQueueStatus('success');
            setTimeout(() => onClose(), 1500);
        } else {
            setQueueStatus('error');
            setImportError(response.error ?? 'Failed to queue action');
        }
    };

    if (alterBookingStateLoading || !data) {
        return (
            <Dialog
                open
                PaperProps={{
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #6366f1',
                        backgroundColor: '#1a1a2e',
                        p: 4,
                    },
                }}
            >
                <SpinnerIcon />
            </Dialog>
        );
    }

    if (error) {
        return (
            <Dialog
                open
                PaperProps={{
                    sx: {
                        borderRadius: 0,
                        border: '4px solid #ef4444',
                        backgroundColor: '#1a1a2e',
                    },
                }}
            >
                <DialogContent sx={{ backgroundColor: '#1a1a2e' }}>
                    <Typography color="error" fontWeight={700}>
                        {(error as Error).toString()}
                    </Typography>
                </DialogContent>
            </Dialog>
        );
    }

    const {
        booking: { country, checkin, checkout, revenue, guestName },
    } = data;

    return (
        <Dialog
            open
            maxWidth="md"
            fullWidth
            PaperProps={{
                sx: {
                    borderRadius: 0,
                    border: '4px solid #6366f1',
                    backgroundColor: '#1a1a2e',
                },
            }}
        >
            <DialogTitle
                sx={{
                    borderBottom: '3px solid #6366f1',
                    backgroundColor: '#0f0f23',
                    fontWeight: 900,
                    fontSize: '1.25rem',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    py: 2,
                }}
            >
                NTAK Import
            </DialogTitle>
            <DialogContent sx={{ backgroundColor: '#1a1a2e', p: 0 }}>
                <Stack sx={{ py: 3, px: 3 }} spacing={2}>
                    {/* Extension Import UI */}
                    {extensionInstalled && !showManualFallback && (
                        <>
                            {importStatus === 'error' && importError && (
                                <Alert
                                    severity="error"
                                    onClose={() => setImportError(null)}
                                    sx={{
                                        borderRadius: 0,
                                        border: '3px solid #ef4444',
                                        backgroundColor: 'rgba(239, 68, 68, 0.1)',
                                        fontWeight: 600,
                                        '& .MuiAlert-icon': {
                                            color: '#ef4444',
                                        },
                                    }}
                                >
                                    {importError}
                                </Alert>
                            )}
                            {importStatus === 'success' && (
                                <Alert
                                    severity="success"
                                    sx={{
                                        borderRadius: 0,
                                        border: '3px solid #22c55e',
                                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                        fontWeight: 600,
                                        '& .MuiAlert-icon': {
                                            color: '#22c55e',
                                        },
                                    }}
                                >
                                    Import completed successfully
                                </Alert>
                            )}
                            {queueStatus === 'success' && (
                                <Alert
                                    severity="success"
                                    sx={{
                                        borderRadius: 0,
                                        border: '3px solid #22c55e',
                                        backgroundColor: 'rgba(34, 197, 94, 0.1)',
                                        fontWeight: 600,
                                        '& .MuiAlert-icon': {
                                            color: '#22c55e',
                                        },
                                    }}
                                >
                                    Action queued. Open the extension popup to execute it on vendegem.hu.
                                </Alert>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#9ca3af',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: '0.75rem',
                                }}
                            >
                                MushRoom Extension detected. Click below to import automatically.
                            </Typography>
                            <Button
                                variant="contained"
                                color="secondary"
                                onClick={handleExtensionImport}
                                disabled={importStatus === 'loading' || importStatus === 'success'}
                                startIcon={importStatus === 'loading' ? <SpinnerIcon /> : undefined}
                                sx={{
                                    borderRadius: 0,
                                    border: '2px solid',
                                    borderColor: 'secondary.main',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 3,
                                    py: 1.5,
                                }}
                            >
                                {importStatus === 'loading' ? 'Importing...' : 'Import via Extension'}
                            </Button>
                            <Button
                                variant="outlined"
                                color="primary"
                                onClick={handleQueueAction}
                                disabled={queueStatus === 'loading' || queueStatus === 'success' || importStatus === 'success'}
                                startIcon={queueStatus === 'loading' ? <SpinnerIcon /> : undefined}
                                sx={{
                                    borderRadius: 0,
                                    border: '2px solid',
                                    fontWeight: 700,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    px: 3,
                                    py: 1.5,
                                    '&:hover': {
                                        borderWidth: '2px',
                                    },
                                }}
                            >
                                {queueStatus === 'loading' ? 'Queueing...' : 'Queue for Later'}
                            </Button>
                            <Button
                                variant="text"
                                onClick={() => setShowManualFallback(true)}
                                sx={{
                                    color: '#6b7280',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: '0.75rem',
                                    alignSelf: 'flex-start',
                                    '&:hover': {
                                        backgroundColor: 'transparent',
                                        color: '#9ca3af',
                                    },
                                }}
                            >
                                Use manual import instead
                            </Button>
                        </>
                    )}

                    {/* Manual Fallback UI */}
                    {(!extensionInstalled || showManualFallback) && (
                        <>
                            {showManualFallback && (
                                <Button
                                    variant="text"
                                    onClick={() => setShowManualFallback(false)}
                                    sx={{
                                        color: '#6b7280',
                                        fontWeight: 600,
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.05em',
                                        fontSize: '0.75rem',
                                        alignSelf: 'flex-start',
                                        '&:hover': {
                                            backgroundColor: 'transparent',
                                            color: '#9ca3af',
                                        },
                                    }}
                                >
                                    ← Back to extension import
                                </Button>
                            )}
                            <Typography
                                variant="body2"
                                sx={{
                                    color: '#9ca3af',
                                    fontWeight: 600,
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.05em',
                                    fontSize: '0.75rem',
                                }}
                            >
                                {!extensionInstalled
                                    ? 'Install the MushRoom extension for automatic import, or copy and paste this script into the browser console on vendegem.hu'
                                    : 'Copy and paste this script into the browser console on vendegem.hu'}
                            </Typography>
                            <TextCopyInput
                        type="textarea"
                        /* spell-checker: disable */
                        value={`
                    (async () => {
                        // Library
                        const monthMap = {
                            'Január': 1,
                            'Február': 2,
                            'Március': 3,
                            'Április': 4,
                            'Május': 5,
                            'Június': 6,
                            'Július': 7,
                            'Augusztus': 8,
                            'Szeptember': 9,
                            'Október': 10,
                            'November': 11,
                            'December': 12,
                        };
                        const sleep = duration => new Promise(resolve => setTimeout(resolve, duration));
                        const triggerTextEditChange = (selector, value) => {
                            $(selector).val(value).focus();
                            $(selector)[0].dispatchEvent(new InputEvent('input', { data: value }));
                            $(selector)[0].dispatchEvent(new UIEvent('change'));
                            $(selector)[0].dispatchEvent(new UIEvent('blur'));
                        };
                        const setDatePickerValue = async (year, month, day) => {
                            console.group('setDatePickerValue');
                            console.log('setDatePickerValue', year, month, day);
                            await sleep(100);
                            if ($("select.ui-datepicker-year").length) {
                                triggerTextEditChange(".ui-datepicker-year", year);
                                triggerTextEditChange(".ui-datepicker-month", month - 1);
                                await sleep(100);
                            } else {
                                console.log($(".ui-datepicker-year"));
                                console.log($(".ui-datepicker-month"));
                                let currentYear = +$(".ui-datepicker-year").text();
                                console.log('currentYear', currentYear);
                                while (currentYear !== year) {
                                    if (currentYear < year) {
                                        $('.ui-datepicker-next')[0].dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, defaultPrevented: true, view: window }));
                                    } else {
                                        // $('.ui-datepicker-prev').click();
                                        $('.ui-datepicker-prev')[0].dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, defaultPrevented: true, view: window }));
                                    }
                                    currentYear = +$(".ui-datepicker-year").text();
                                    console.log('currentYear', currentYear);
                                }

                                let currentMonth = monthMap[$(".ui-datepicker-month").text()];
                                console.log('currentMonth', currentMonth);
                                await sleep(100);

                                while (currentMonth !== month) {
                                    if (currentMonth < month) {
                                        console.log('Next', $('.ui-datepicker-next')[0]);
                                        // $('.ui-datepicker-next').click();
                                        $('.ui-datepicker-next')[0].dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, defaultPrevented: true, view: window }));
                                    } else {
                                        console.log('Prev', $('.ui-datepicker-next')[0]);
                                        // $('.ui-datepicker-prev').click();
                                        $('.ui-datepicker-prev')[0].dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, defaultPrevented: true, view: window }));
                                    }
                                    await sleep(100);
                                    currentMonth = monthMap[$(".ui-datepicker-month").text()];
                                    console.log('currentMonth', currentMonth);
                                    await sleep(100);
                                }
                            }
                            await sleep(100);
                            const dayButton = $(".ui-datepicker-calendar a")
                                .toArray().find(e => e.innerText.trim() === day.toString());
                            if (!dayButton) {
                                console.error('dayButton not found');
                                console.log($(".ui-datepicker-calendar a")
                                .toArray().map(e => e.innerText.trim()));
                                debugger;
                                throw new Error(\`Cannot find button: \${day}\`);
                                /* await (new Promise(resolve => {
                                    const handler = () => {
                                        $(document).off('click', '.ui-datepicker-calendar a', handler);
                                        resolve();
                                    };
                                    $(document).on('click', '.ui-datepicker-calendar a', handler);
                                }));
                                */
                            } else {
                                console.log('Click', dayButton);
                                dayButton.dispatchEvent(new MouseEvent('click'));
                            }
                            await sleep(100);
                            console.log('Success');
                            console.groupEnd('setDatePickerValue');
                        };
                        const openDatePicker = (selector) => {
                            $(selector + " .ui-datepicker-trigger").click();
                        };
                        const selectDate = async (selector, year, month, day) => {
                            openDatePicker(selector);
                            return setDatePickerValue(year, month, day);
                        };
                        const selectDropdownItem = async (selector, value) => {
                            $(selector + " label").click();
                            await sleep(100);
                            $($(".ui-dropdown-item")[value + 1]).click()
                        };
                        const selectDropdownValue = async (selector, value) => {
                            $(selector + " label").click();
                            await sleep(100);
                            $($(".ui-dropdown-item").toArray().filter(item => item.innerText === value)).click()
                        };
                        const openDropdownItem = async selector => {
                            $(selector + " label").click();
                            return new Promise(resolve => {
                                const handler = () => {
                                    $(document).off('click', '.ui-dropdown-item', handler);
                                    resolve();
                                };
                                $(document).on('click', '.ui-dropdown-item', handler);
                            });
                        };

                        const click = elementOrSelector => {
                            let element;
                            if (typeof elementOrSelector === 'string') {
                                element = document.querySelector(elementOrSelector);
                            } else {
                                element = elementOrSelector;
                            }
                            element.dispatchEvent(new MouseEvent('click', { bubbles: false, cancelable: true, view: window }));
                        };

                        // Instructions

                        location.hash = '/naptarnezet';
                        await sleep(500);
                        $("#foglalasokAtTekintese button").click();
                        await sleep(500);
                        $("#ujFoglalasGomb button").click();
                        await sleep(500);

                        openDatePicker("#erkezesUtazasDatuma_0");
                        await setDatePickerValue(${checkin.slice(
                            0,
                            4
                        )}, ${checkin
                            .slice(5, 7)
                            .replace(/^0?([^0].*)$/g, '$1')}, ${checkin
                            .slice(8)
                            .replace(/^0?([^0].*)$/g, '$1')});
                        await sleep(100);
                        await setDatePickerValue(${checkout.slice(
                            0,
                            4
                        )}, ${checkout
                            .slice(5, 7)
                            .replace(/^0?([^0].*)$/g, '$1')}, ${checkout
                            .slice(8)
                            .replace(/^0?([^0].*)$/g, '$1')});

                        await sleep(100);
                        $('app-naptar-gombok .ui-button-secondary').click();
                        await sleep(100);

                        await selectDropdownItem("#lakoegysegAzonosito_0", 0);

                        triggerTextEditChange("#teljesAr_0 input", "${revenue}");


                        await sleep(1000);
                        click('.green-button button');

                        triggerTextEditChange("#megrendelo-nev-input", "${guestName}");

                        await selectDropdownItem("#foglalasMod", 3);

                        await selectDropdownValue("#megrendeloAllampolgarsag", "${
                            country.nationalityHU
                        }");

                        await sleep(100);
                        click('.green-button button');

                        await sleep(100);
                        click('.green-button button');

                        click(document.querySelector('.ui-dropdown-label[aria-label="Foglalás csatornája"]').parentElement);

                        click(Array.from(document.querySelectorAll('.ui-dropdown-item')).find(item => item.innerText === 'Közvetítő-online'))

                        click('#megrendeloAllampolgarsag .ui-dropdown');

                        click(Array.from(document.querySelectorAll('.ui-dropdown-item')).find(item => item.innerText === '${
                            country.nationalityHU
                        }'))

                        console.log('Finishing');
                        await sleep(100);
                        click('.green-button button');
                    })().catch(error => {
                        throw error;
                    });
                    
                    `
                            .split('\n')
                            .map(line => line.slice(5 * 4))
                            .join('\n')
                            .trim()}
                        /* spell-checker: enable */
                    />
                        </>
                    )}
                </Stack>
            </DialogContent>
            <DialogActions
                sx={{
                    borderTop: '3px solid #374151',
                    backgroundColor: '#0f0f23',
                    p: 3,
                    gap: 1.5,
                    justifyContent: 'flex-end',
                }}
            >
                <Button
                    variant="outlined"
                    onClick={onClose}
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        borderColor: '#374151',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                        '&:hover': {
                            borderWidth: '2px',
                            borderColor: '#374151',
                            backgroundColor: 'rgba(55, 65, 81, 0.1)',
                        },
                    }}
                >
                    Close
                </Button>
                <Button
                    variant="outlined"
                    color="primary"
                    onClick={() => {
                        window.open(
                            'https://vendegem.hu',
                            '_blank',
                            'noopener noreferrer'
                        );
                    }}
                    sx={{
                        borderRadius: 0,
                        borderWidth: '2px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                        '&:hover': {
                            borderWidth: '2px',
                        },
                    }}
                >
                    Open NTAK
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => {
                        alterBookingState({
                            variables: {
                                id: bookingId,
                                fields: {
                                    importedToGovernmentPortal: true,
                                },
                            },
                        })
                            .then(() => {
                                onSuccess?.();
                            })
                            .catch(saveError => {
                                throw saveError;
                            })
                            .finally(() => {
                                onClose();
                            });
                    }}
                    sx={{
                        borderRadius: 0,
                        border: '2px solid #6366f1',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.05em',
                        px: 3,
                        py: 1,
                    }}
                >
                    Mark as Imported
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default GuestBookView;
